/**
 * @file document-coordinator.ts
 * @description Handles zoom synchronization, document switching, and tab management.
 */

import { g, appEvents } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { getHistoryForDoc } from './history-coordinator';

export function updateZoomUI() {
    const zoomSlider = document.getElementById('zoomSlider') as HTMLInputElement;
    const zoomDisplay = document.getElementById('zoomDisplay');
    const zoomPercent = Math.round((g.zoom || 1) * 100);
    if (zoomSlider) zoomSlider.value = zoomPercent.toString();
    if (zoomDisplay) zoomDisplay.innerText = zoomPercent + '%';
}

export function initDocumentCoordinator() {
    const zoomSlider = document.getElementById('zoomSlider') as HTMLInputElement;
    const zoomDisplay = document.getElementById('zoomDisplay');

    if (zoomSlider) {
        zoomSlider.addEventListener('input', () => {
            const val = parseInt(zoomSlider.value);
            const oldZoom = g.zoom;
            const newZoom = val / 100;
            
            // Standardize zoom setting
            g.zoom = newZoom;

            if ((window as any).applyZoom) {
                (window as any).applyZoom(oldZoom, null);
            } else {
                console.warn('[DocumentCoordinator] window.applyZoom not found, falling back to manual refresh.');
                EventBus.emit('RENDER_LAYERS');
                if (zoomDisplay) zoomDisplay.innerText = Math.round(newZoom * 100) + '%';
            }
            
            if ((window as any).updateSVGSelection) (window as any).updateSVGSelection();
        });
    }

    // Manual zoom sync
    window.addEventListener('zoomChanged', updateZoomUI);
    // Initial sync
    setTimeout(updateZoomUI, 100);

    // Document switching
    EventBus.on('DOCUMENT_SWITCHED', (data: { index: number }) => {
        const doc = g.documents[data.index] as any;
        if (doc) {
            // #UndoRedoFix: Ensure document history is initialized/patched
            getHistoryForDoc(doc.id);
            
            updateZoomUI();
            
            // Reset visual zoom to match the newly switched document's zoom
            if ((window as any).applyZoom) {
                (window as any).applyZoom(g.zoom, null);
            }
        }
    });

    // Tab rendering implementation since core logic was removed
    const renderTabsImpl = function() {
        const container = document.getElementById('imageTabBar');
        if (!container) return;

        const docs = g.documents || [];
        container.innerHTML = '';
        
        if (docs.length === 0) {
            document.body.classList.add('no-document-active');
            return;
        }
        
        document.body.classList.remove('no-document-active');

        docs.forEach((doc: any, i: number) => {
            const tab = document.createElement('div');
            tab.className = `image-tab ${i === g.activeDocumentIndex ? 'active' : ''}`;
            tab.dataset.index = i.toString();
            
            const title = document.createElement('span');
            title.className = 'image-tab-name';
            title.innerText = (doc.name || 'Untitled') + (doc.modified ? '*' : '');
            
            const closeBtn = document.createElement('span');
            closeBtn.className = 'image-tab-close';
            closeBtn.innerText = '×';
            closeBtn.onclick = (e) => {
                e.stopPropagation();
                if (typeof (window as any).closeDocument === 'function') {
                    (window as any).closeDocument(i);
                } else {
                    EventBus.emit('CLOSE_DOCUMENT', { index: i });
                }
            };
            
            tab.appendChild(title);
            tab.appendChild(closeBtn);
            container.appendChild(tab);

            title.onclick = (e: Event) => {
                e.stopPropagation();
                if (typeof (window as any).switchDocument === 'function') {
                    (window as any).switchDocument(i);
                } else {
                    EventBus.emit('SWITCH_DOCUMENT', { index: i });
                }
            };
        });
    };

    (window as any).renderImageTabs = renderTabsImpl;
    
    // Auto-render tabs on init
    if ((window as any).renderImageTabs) (window as any).renderImageTabs();
    
    // Sync Window menu list
    renderWindowMenuDocuments();

    EventBus.on('DOCUMENT_STATE_CHANGED', () => {
        if ((window as any).renderImageTabs) (window as any).renderImageTabs();
        renderWindowMenuDocuments();
    });

    EventBus.on('DOCUMENT_TABS_CHANGED', () => {
        if ((window as any).renderImageTabs) (window as any).renderImageTabs();
        renderWindowMenuDocuments();
    });

    EventBus.on('DOCUMENT_SWITCHED', () => {
        renderWindowMenuDocuments();
    });

    // ─── Unsaved Changes Interception (#2069) ─────────────────
    
    // Store original core function if it was already on window
    const originalCloseDocument = (window as any).closeDocument;
    let pendingCloseIndex: number | null = null;

    /**
     * Intercepts the close request to check for unsaved changes.
     */
    /**
     * Intercepts the close request to check for unsaved changes.
     */
    const closeDocumentInterceptor = (index: number) => {
        const doc = g.documents[index] as any;
        if (!doc) return;

        console.log(`[BUG 2069-A] closeDocument called index: ${index}, name: ${doc.name}, modified: ${doc.modified}`);

        if (doc.modified) {
            console.log(`[BUG 2069-A] Showing unsaved changes modal for ${doc.name}`);
            pendingCloseIndex = index;
            showUnsavedChangesModal(doc.name);
        } else {
            console.log(`[BUG 2069-A] Closing document ${doc.name} immediately (not modified)`);
            executeClose(index);
        }
    };

    // Expose to window but allow legacy modules to see it
    (window as any).closeDocument = closeDocumentInterceptor;

    function executeClose(index: number) {
        // We emit the signal that core/document.ts handles
        EventBus.emit('CLOSE_DOCUMENT_CORE', { index });
    }

    // Double-interception: Also listen for the raw event signal in case someone else triggers it
    EventBus.on('CLOSE_DOCUMENT', (data: { index: number }) => {
        const doc = g.documents[data.index] as any;
        if (!doc) return;

        if (doc.modified && pendingCloseIndex === null) {
            console.log(`[BUG 2069-A] Intercepted CLOSE_DOCUMENT event for ${doc.name}`);
            pendingCloseIndex = data.index;
            showUnsavedChangesModal(doc.name);
            // We can't stop the EventBus event since it's fire-and-forget,
            // but the core listener only reacts to CLOSE_DOCUMENT_CORE.
        }
    });

    function showUnsavedChangesModal(filename: string) {
        const modal = document.getElementById('unsavedChangesModal');
        const nameEl = document.getElementById('unsavedFilename');
        if (modal && nameEl) {
            nameEl.textContent = filename;
            modal.style.display = 'flex';
        }
    }

    function hideUnsavedChangesModal() {
        const modal = document.getElementById('unsavedChangesModal');
        if (modal) modal.style.display = 'none';
        pendingCloseIndex = null;
    }

    // Capture global window/tab close (Browser)
    window.onbeforeunload = (e) => {
        const hasModified = g.documents.some((d: any) => d.modified);
        if (hasModified) {
            e.preventDefault();
            e.returnValue = ''; // Standard way to show browser confirmation
            return '';
        }
    };

    // [CRITICAL_LOGIC_START]
    // Capture Tauri native window close (#2069-A)
    const setupTauriCloseInterception = async () => {
        const tauri = (window as any).__TAURI__;
        if (tauri && tauri.window) {
            const currentWindow = tauri.window.getCurrent();
            await currentWindow.onCloseRequested(async (event: any) => {
                const hasModified = g.documents.some((d: any) => d.modified);
                if (hasModified) {
                    event.preventDefault(); // Stop Tauri from closing
                    
                    // Show our own UI modal for the first modified document
                    const firstModifiedIndex = g.documents.findIndex((d: any) => d.modified);
                    if (firstModifiedIndex !== -1) {
                        pendingCloseIndex = firstModifiedIndex;
                        showUnsavedChangesModal(g.documents[firstModifiedIndex].name);
                    }
                }
            });
            console.log("[Document-Coordinator] Tauri close interception active.");
        }
    };
    setupTauriCloseInterception();
    // [CRITICAL_LOGIC_END]

    // Modal Button Listeners
    document.getElementById('cancelCloseBtn')?.addEventListener('click', hideUnsavedChangesModal);

    document.getElementById('discardChangesBtn')?.addEventListener('click', () => {
        if (pendingCloseIndex !== null) {
            executeClose(pendingCloseIndex);
        }
        hideUnsavedChangesModal();
    });

    document.getElementById('saveChangesBtn')?.addEventListener('click', () => {
        if (pendingCloseIndex !== null) {
            // Trigger Save. We assume Save is successful and will call hide/close.
            // In a more robust system, we would await successful save.
            if ((window as any).saveProject) {
                (window as any).saveProject();
                
                // Reset modification flag after save trigger
                const doc = g.documents[pendingCloseIndex] as any;
                if (doc) doc.modified = false;
                
                executeClose(pendingCloseIndex);
            }
        }
        hideUnsavedChangesModal();
    });

    (window as any).renderWindowMenuDocuments = renderWindowMenuDocuments;
}

/**
 * Renders the list of open documents in the Window menu section.
 * #2072 implementation.
 */
function renderWindowMenuDocuments(): void {
    const container = document.getElementById('windowMenuDocs');
    if (!container || !g || !g.documents) return;
    
    container.innerHTML = '';
    
    // Limit to 20 documents (#2072)
    const docs = g.documents.slice(0, 20);
    
    docs.forEach((doc: any, index: number) => {
        const option = document.createElement('div');
        option.className = 'menu-option';
        if (index === g.activeDocumentIndex) {
            option.classList.add('active-doc'); // Optional: special styling
        }
        
        option.onclick = (e) => {
            e.stopPropagation();
            if ((window as any).switchDocument) {
                (window as any).switchDocument(index);
            } else {
                EventBus.emit('SWITCH_DOCUMENT', { index });
            }
            if ((window as any).closeAllMenus) (window as any).closeAllMenus();
        };

        const content = document.createElement('div');
        content.className = 'menu-option-content';
        content.style.padding = '4px 12px';
        content.style.minWidth = '180px';
        content.style.display = 'flex';
        content.style.alignItems = 'center';
        
        // Active indicator (check mark)
        const check = document.createElement('span');
        check.style.width = '20px';
        check.style.display = 'inline-block';
        check.style.fontSize = '12px';
        check.innerHTML = index === g.activeDocumentIndex ? '✓' : '';
        check.style.color = 'var(--accent-primary, #0078d7)';
        
        const name = document.createElement('span');
        name.style.fontSize = '12px';
        name.textContent = (doc.name || 'Untitled') + (doc.modified ? '*' : '');
        
        content.appendChild(check);
        content.appendChild(name);
        option.appendChild(content);
        container.appendChild(option);
    });
}

/**
 * Note: core/document.ts needs to be updated to match the CLOSE_DOCUMENT_CORE signal
 * or we just call the original if we can capture it properly.
 * However, since we are using a singleton pattern, EventBus is safest.
 */
